/**
 * Ideas (R8.4, Phase 6, ADR-0002 addendum 2026-07-07, "D-Ideas"):
 * category-tagged capture, promotable ONLY to an objective action (never to
 * a Priority, unlike a brain-dump note -- spec §2.3's table). `promoteIdea`
 * takes only `objectiveId` at the DomainApi layer (never a `target: {kind}`
 * parameter), so "no promote-to-Priority path" (AC-T12) is enforced by the
 * method signature, not a runtime rejection.
 */

import type {
  ActionId,
  ClockPort,
  CompassEvent,
  IdeaCategory,
  IdeaCountsView,
  IdeaCreatedEvent,
  IdeaDeletedEvent,
  IdeaId,
  IdeaPromotedEvent,
  IdeaView,
  IdPort,
  IsoDate,
  ObjectiveId,
} from '../../contracts';
import { type CommandResult, domainError } from '../event-log/command-error';
import { eventsOfTypes } from '../event-log/event-log';

type Ports = { readonly clock: ClockPort; readonly id: IdPort };

interface IdeaState {
  readonly ideaId: IdeaId;
  readonly text: string;
  readonly category: IdeaCategory;
  readonly createdOn: string;
  gone: boolean;
}

function buildIdeaStates(events: readonly CompassEvent[]): Map<IdeaId, IdeaState> {
  const states = new Map<IdeaId, IdeaState>();
  for (const event of eventsOfTypes(events, ['idea.created', 'idea.deleted', 'idea.promoted'])) {
    switch (event.type) {
      case 'idea.created':
        states.set(event.ideaId, {
          ideaId: event.ideaId,
          text: event.text,
          category: event.category,
          createdOn: event.occurredOn,
          gone: false,
        });
        break;
      case 'idea.deleted': {
        const state = states.get(event.ideaId);
        if (state) state.gone = true;
        break;
      }
      case 'idea.promoted': {
        const state = states.get(event.ideaId);
        if (state) state.gone = true;
        break;
      }
      default:
        break;
    }
  }
  return states;
}

/** Every idea never deleted or promoted. Unsorted, mirrors `listNotes`'s convention. */
export function listIdeas(events: readonly CompassEvent[]): readonly IdeaView[] {
  const states = buildIdeaStates(events);
  const views: IdeaView[] = [];
  for (const state of states.values()) {
    if (state.gone) continue;
    views.push({ ideaId: state.ideaId, text: state.text, category: state.category });
  }
  return views;
}

/**
 * Daily/monthly idea creation counts (AC-T13) -- a pure count over
 * `idea.created` timestamps, no new math beyond counting, mirrors
 * `CompletionHistoryMonthView`'s existing "count query over already-
 * permanent events" pattern (ADR-0011). Counts EVERY `idea.created` event in
 * the window regardless of whether the idea was later deleted/promoted --
 * "created" is a permanent historical fact, unaffected by what happened to
 * the idea afterward (mirrors how a completed action still counts in
 * completion history even if later reverted-and-recompleted elsewhere).
 */
export function getIdeaCounts(
  events: readonly CompassEvent[],
  today: IsoDate | string,
): IdeaCountsView {
  const monthPrefix = today.slice(0, 7); // 'YYYY-MM'
  let createdToday = 0;
  let createdThisMonth = 0;
  for (const event of eventsOfTypes(events, ['idea.created'])) {
    if (event.occurredOn === today) createdToday += 1;
    if (event.occurredOn.slice(0, 7) === monthPrefix) createdThisMonth += 1;
  }
  return { createdToday, createdThisMonth };
}

export function createIdea(
  text: string,
  category: IdeaCategory,
  ports: Ports,
): CommandResult<IdeaCreatedEvent> {
  if (text.trim().length === 0) {
    throw domainError('invalid_input', 'idea text must not be empty');
  }

  const event: IdeaCreatedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'idea.created',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    ideaId: ports.id.nextRawId() as IdeaId,
    text,
    category,
  };

  return { events: [event] };
}

function requireLiveIdea(events: readonly CompassEvent[], ideaId: IdeaId): IdeaState {
  const state = buildIdeaStates(events).get(ideaId);
  if (!state || state.gone) {
    throw domainError('idea_not_found', `idea ${ideaId} not found`);
  }
  return state;
}

export function deleteIdea(
  events: readonly CompassEvent[],
  ideaId: IdeaId,
  ports: Ports,
): CommandResult<IdeaDeletedEvent> {
  requireLiveIdea(events, ideaId);

  const event: IdeaDeletedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'idea.deleted',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    ideaId,
  };

  return { events: [event] };
}

/**
 * One-way, consumes the source (spec §2.3), mirrors `promoteNote`'s
 * sequencing rule: the `action.added` event this references must be
 * appended FIRST, in log order (caller's responsibility -- the DomainApi
 * layer mints the action via `objectives/commands.ts`'s `addAction` before
 * calling this). No `NotePromotionTarget`-shaped union -- an idea has
 * exactly one legal target shape, typed directly (illegal states
 * unrepresentable).
 */
export function promoteIdea(
  events: readonly CompassEvent[],
  ideaId: IdeaId,
  objectiveId: ObjectiveId,
  actionId: ActionId,
  ports: Ports,
): CommandResult<IdeaPromotedEvent> {
  requireLiveIdea(events, ideaId);

  const event: IdeaPromotedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'idea.promoted',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    ideaId,
    objectiveId,
    actionId,
  };

  return { events: [event] };
}
